import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import { getCancelInfo, cancelBooking } from '../lib/appointments';
import SEO from '../components/SEO';
import { SEO_TEXTES_HORS_PRERENDU } from '../data/seoTextes';

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-50px' },
  transition: { duration: 0.5, ease: 'easeOut' },
};

export default function AnnulerRdv() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Lien invalide : aucun token fourni.');
      return;
    }
    getCancelInfo(token)
      .then(setInfo)
      .catch((e) => setError(e.message));
  }, [token]);

  // Annulation tardive si moins de 48 heures avant le RDV (aligné backend).
  const isLate =
    info &&
    (new Date(info.start_at).getTime() - new Date().getTime()) / (1000 * 60 * 60) < 48;

  const confirmCancel = async () => {
    setSubmitting(true);
    try {
      const r = await cancelBooking(token);
      setDone(r);
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEO {...SEO_TEXTES_HORS_PRERENDU['/prendre-rdv/annuler']} path="/prendre-rdv/annuler" noindex />
    <main data-testid="annuler-rdv-page">
      <section className="pt-32 pb-12">
        <div className="max-w-2xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp}>
            <p className="text-sm font-medium tracking-[0.2em] uppercase text-[#0099D6] mb-4">
              MÉTÉRIS — Rendez-vous
            </p>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-white mb-3">
              Annulation
            </h1>
            <p className="text-slate-300">
              Annulation libre jusqu'à 48 heures avant le RDV.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-2xl mx-auto px-6 md:px-12">
          <motion.div
            {...fadeUp}
            className="bg-[#15171E]/80 backdrop-blur-xl border border-white/5 rounded-2xl p-6 md:p-10"
          >
            {error && (
              <div
                className="flex items-start gap-3 p-4 bg-red-500/10 border border-red-400/30 rounded-lg text-red-300"
                data-testid="cancel-error"
              >
                <XCircle size={22} className="flex-shrink-0 mt-0.5" />
                <p className="text-sm">{error}</p>
              </div>
            )}

            {!error && !info && !done && (
              <p className="text-center text-slate-400 text-sm" data-testid="cancel-loading">
                Chargement…
              </p>
            )}

            {done && (
              <div className="text-center py-6" data-testid="cancel-done">
                <div className="w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-400/30 flex items-center justify-center mx-auto mb-5">
                  <CheckCircle className="w-8 h-8 text-emerald-400" />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
                  Rendez-vous annulé
                </h2>
                <p className="text-slate-300 mb-3">
                  Statut : <strong className="text-[#0099D6]">{done.status}</strong>
                </p>
                {done.to_invoice && (
                  <div className="mt-5 p-4 bg-amber-500/10 border border-amber-400/30 rounded-lg text-sm text-amber-200 text-left">
                    <strong>Facturation :</strong> conformément aux conditions de réservation, ce
                    créneau pourra faire l'objet d'une facturation (annulation tardive à moins de
                    48 heures du RDV).
                  </div>
                )}
              </div>
            )}

            {info && !done && !error && (
              <>
                <h2 className="text-2xl font-bold text-white mb-5">
                  Annuler votre rendez-vous
                </h2>

                <div className="border border-white/10 p-4 rounded-lg mb-5 text-slate-300 space-y-2 bg-white/[0.02]">
                  <p>
                    <span className="text-slate-500">Date :</span>{' '}
                    <strong className="text-white">
                      {new Date(info.start_at).toLocaleString('fr-FR', {
                        weekday: 'long',
                        day: 'numeric',
                        month: 'long',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </strong>
                  </p>
                  <p>
                    <span className="text-slate-500">Au nom de :</span>{' '}
                    <strong className="text-white">
                      {info.prenom} {info.nom}
                    </strong>
                  </p>
                  {info.motif && (
                    <p>
                      <span className="text-slate-500">Motif :</span>{' '}
                      <span className="text-slate-300">{info.motif}</span>
                    </p>
                  )}
                </div>

                {isLate && (
                  <div className="flex items-start gap-3 mb-5 p-4 bg-amber-500/10 border border-amber-400/30 rounded-lg text-sm text-amber-200">
                    <AlertTriangle size={20} className="flex-shrink-0 mt-0.5" />
                    <p>
                      Cette annulation est <strong>tardive (moins de 48 heures du RDV)</strong>.
                      Conformément aux conditions de réservation, ce créneau pourra faire l'objet
                      d'une facturation de <strong>20 € TTC</strong>.
                    </p>
                  </div>
                )}

                <button
                  type="button"
                  onClick={confirmCancel}
                  disabled={submitting}
                  className="w-full py-3 px-6 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-all hover:scale-[1.02] active:scale-95 disabled:opacity-50 disabled:hover:scale-100"
                  data-testid="confirm-cancel"
                >
                  {submitting ? 'Annulation…' : "Confirmer l'annulation"}
                </button>
              </>
            )}
          </motion.div>
        </div>
      </section>
    </main>
    </>
  );
}
